import InputWithComma from "components/Input/InputWithComma/InputWithComma"
import { Keyboard } from "hocs/withKeyboard"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { GlobalStore } from "types/store"
import { Button } from "components/Button"
import { languages } from "lib/languages"
import { PATH } from "models/survey/_paths"
import PreviousButton from "components/Previous/PreviousButton"
import { SURVEY_PAGE } from "core/constants"
import { useNavigate } from "react-router"
import surveyActions from "containers/Survey/services/actions"
import { ACTION, Reply } from "containers/Survey/types"
import { Title } from "components/Title/Title"
import styles from "./Lenses.module.scss"


export default function Lenses(): JSX.Element {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const question = useSelector((state: GlobalStore) => state.survey.current)
    const storedValue = useSelector((state: GlobalStore) =>
        state.survey.answers.find(({ questionId }) => questionId === question?.id)?.value?.[0]
    )
    const [keyboardValue, setKeyboardValue] = useState("")

    const decimalScale = question?.keyboard?.decimalScale ?? 0

    useEffect(() => {
        setKeyboardValue(
            typeof storedValue === "number"
                ? String(Math.round(storedValue * 10 ** decimalScale))
                : ""
        )
    }, [question?.id, decimalScale, storedValue])

    const keyboardToReplyValue = (value: string): number => {
        const scale = question?.keyboard?.decimalScale ?? 0
        if (!scale) return Number(value)
        const normalized =
            value.length <= scale
                ? `0.${value.padStart(scale, "0")}`
                : `${value.slice(0, -scale)}.${value.slice(-scale)}`
        return Number.parseFloat(normalized)
    }

    const keyboardDisplayValue = () =>
        keyboardToReplyValue(keyboardValue)
            .toFixed(question?.keyboard?.decimalScale ?? 0)
            .replace(".", ",")

    const handleNext = () => {
        if (!question?.id || !keyboardValue.length) return
        const reply: Reply = {
            title: keyboardDisplayValue(),
            value: keyboardToReplyValue(keyboardValue),
            action: question.subquestion ? ACTION.subquestion : ACTION.default,
        }
        dispatch(surveyActions.unsetStepReplies({ id: question.id }))
        dispatch(surveyActions.setStepReplies({ replies: [reply] }))
        dispatch(surveyActions.next())
        navigate(SURVEY_PAGE)
    }

    const handleSkip = () => {
        if (!question?.id) return
        const skippedReply: Reply = {
            title: languages.IDontKnow,
            value: null,
            action: question.subquestion ? ACTION.subquestion : ACTION.default,
        }
        dispatch(surveyActions.unsetStepReplies({ id: question.id }))
        dispatch(surveyActions.setStepReplies({ replies: [skippedReply] }))
        dispatch(surveyActions.next())
        setKeyboardValue("")
        if (!question.subquestion) {
            navigate(SURVEY_PAGE)
        }
    }

    return (
        <>
            <PreviousButton onClick={() => {
                dispatch(surveyActions.previous())
                navigate(SURVEY_PAGE)
            }} />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", width: "100%", height: "100%" }}>
                <div style={{ width: "80%" }}>
                    <Title size="large" text={question.title} style={{ marginBottom: 0 }} />
                </div>
                <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" }}>
                    <div style={{ width: "1200px", height: "450px" }}>
                        <Keyboard
                            value={keyboardValue}
                            onChange={(value: string) => setKeyboardValue(value)}
                            options={{
                                type: "code",
                                inputName: "lenses-keyboard",
                                unit: "mm",
                            }}
                            hideResetButton={true}
                        >
                            <InputWithComma value={keyboardValue} name="lenses-keyboard" />
                        </Keyboard>
                    </div>
                    <Button wide="long" type="primary" disabled={!keyboardValue.length} onClick={handleNext}>
                        {languages.confirm}
                    </Button>
                    <Button className={styles.SmallLink} type="link" onClick={handleSkip}>
                        {languages.nirManualInput}
                    </Button>
                </div>
                {
                    [PATH.contact_lens_type, PATH.contact_lens_brand, PATH.contact_lens_bending_radius, PATH.contact_lens_diameter].includes(question.id) && (
                        <div style={{ marginTop: "24px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                            <div>
                                <span style={{ fontSize: "32px" }}>{languages.askToOptician}</span>
                            </div>
                        </div>
                    )
                }
            </div>
        </>
    )
}
